import { useEffect, useState } from "react";
import { Alert, Empty, Segmented, Skeleton, Tag, Typography, theme } from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  EditOutlined,
  FileTextOutlined,
  FlagOutlined,
  MessageOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { ApiService } from "../api";
import type { CaseTimelineEntry } from "../api";
import { apiErrorMessage } from "../api/apiError";

const { Text } = Typography;

type Props = {
  requestId: string | number;
  /** Bumped by the page when anything changes, so the story stays current
   *  without this component knowing what changed. */
  refresh?: number;
};

/** What each kind looks like. An icon *and* a colour: colour alone is the only
 *  cue for nobody, and these six have to be separable in a long list. */
const LOOK: Record<
  string,
  { icon: React.ReactNode; color: string; label: string }
> = {
  status: { icon: <FlagOutlined />, color: "blue", label: "Moved" },
  approval: { icon: <CheckCircleOutlined />, color: "green", label: "Signed" },
  rejection: { icon: <CloseCircleOutlined />, color: "red", label: "Sent back" },
  section: { icon: <FileTextOutlined />, color: "cyan", label: "Section" },
  comment: { icon: <MessageOutlined />, color: "gold", label: "Comment" },
  edit: { icon: <EditOutlined />, color: "default", label: "Edited" },
  other: { icon: <EditOutlined />, color: "default", label: "Activity" },
};

/** Filters, in the order somebody narrows: everything, then the decisions, then
 *  what people said. Deliberately three and not seven — a filter per kind is a
 *  second thing to learn. */
const FILTERS = [
  { value: "all", label: "Everything" },
  { value: "decisions", label: "Decisions only" },
  { value: "comments", label: "Comments" },
] as const;

const KEEP: Record<string, (kind: string) => boolean> = {
  all: () => true,
  decisions: (kind) =>
    kind === "status" || kind === "approval" || kind === "rejection",
  comments: (kind) => kind === "comment",
};

/**
 * The whole story of one case, in order.
 *
 * A case leaves traces in four tables and the story is in none of them. Before
 * this, reading what happened meant opening the Audit Trail dialog, then the
 * comments panel, then the approvals list, and holding three orderings in your
 * head.
 *
 * **Newest first here, oldest first on the server.** The server returns a
 * story, which starts at the beginning; this is a panel somebody glances at to
 * see what just happened. Reversing is a display choice and belongs where the
 * display is.
 *
 * The default filter is everything, including the edits — collapsed into one
 * line per person per sitting by the server, which is what makes "everything"
 * readable at all. Hiding them would be lying by omission; a hundred identical
 * lines would bury the six that matter.
 */
const CaseTimeline = ({ requestId, refresh }: Props) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const [entries, setEntries] = useState<CaseTimelineEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState<string>("all");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    ApiService.apiMainRequestTimelineRetrieve(Number(requestId))
      .then((data) => {
        if (!cancelled) {
          setEntries(data.entries ?? []);
          setError("");
        }
      })
      .catch((e) => {
        if (!cancelled) setError(apiErrorMessage(e, t("Something went wrong")));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [requestId, refresh, t]);

  const shown = entries.filter((e) => KEEP[filter](e.kind)).slice().reverse();

  if (loading && entries.length === 0) {
    return <Skeleton active paragraph={{ rows: 4 }} title={false} />;
  }
  if (error) return <Alert type="warning" showIcon message={error} />;
  if (entries.length === 0) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={t("Nothing has happened on this case yet.")}
      />
    );
  }

  return (
    <div>
      <Segmented
        size="small"
        value={filter}
        onChange={(v) => setFilter(String(v))}
        options={FILTERS.map((f) => ({ value: f.value, label: t(f.label) }))}
        style={{ marginBottom: 12 }}
      />

      {shown.length === 0 ? (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {t("Nothing of that kind on this case.")}
        </Text>
      ) : (
        <div>
          {shown.map((entry, index) => {
            const look = LOOK[entry.kind] ?? LOOK.other;
            return (
              <div
                key={`${entry.at}-${index}`}
                style={{
                  display: "flex",
                  gap: 10,
                  padding: "8px 0",
                  borderTop:
                    index > 0 ? `1px solid ${token.colorSplit}` : undefined,
                }}
              >
                <span
                  style={{
                    color: token.colorTextTertiary,
                    marginTop: 2,
                    flex: "0 0 auto",
                  }}
                >
                  {look.icon}
                </span>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 13 }}>
                    <strong>{entry.actor}</strong>{" "}
                    <span style={{ color: token.colorTextSecondary }}>
                      {entry.text || t(look.label)}
                    </span>
                    {entry.count > 1 && (
                      <Tag style={{ marginLeft: 6 }}>
                        {t("×{{count}}", { count: entry.count })}
                      </Tag>
                    )}
                  </div>
                  <div
                    style={{ fontSize: 11, color: token.colorTextTertiary }}
                  >
                    {new Date(entry.at).toLocaleString()}
                    {entry.to_status && (
                      <>
                        {" · "}
                        {t("now {{status}}", { status: entry.to_status })}
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CaseTimeline;
